


import {
  defineComponent,
  computed,
  reactive,
  PropType,
} from '@vue/composition-api';
import MyCalendar from '@/components/lib/MyCalendar.vue';
import Pagination from '@/components/lib/Pagination.vue';
import Modal from '@/components/lib/Modal.vue';
import { createInitialSearchParams } from '@/models/notification';
import { NotificationIndexParams } from '@/models/apis/notification/notificationRequest';
import { Notification } from '@/models/apis/notification/notificationResponse';

export interface NotificationTableState {
  search: NotificationIndexParams;
  notificationToDelete: Notification | null;
  isLoading: boolean;
  showDeleteModal: boolean;
  pager: {
    currentPage: number;
    itemsPerPage: number;
  };
}

type NotificationStatusStyle = {
  'background-color': string;
};

const NOTIFICATION_COLORS = {
  DEFAULT: '#ffffff',
  UPCOMING: '#88cb7f',
  EXPIRED: '#adadad',
} as const;

export default defineComponent({
  components: {
    Modal,
    MyCalendar,
    Pagination,
  },
  props: {
    notifications: {
      type: Array as PropType<Notification[]>,
      default: () => [],
    },
    search: {
      type: Object as PropType<NotificationIndexParams>,
      default: () => createInitialSearchParams(),
    },
  },
  emits: ['search-notification', 'update-notification', 'delete-notification'],

  setup(props, { emit }) {
    const state = reactive<NotificationTableState>({
      search: props.search,
      notificationToDelete: null,
      isLoading: false,
      showDeleteModal: false,
      pager: {
        currentPage: 1,
        itemsPerPage: 50,
      },
    });

    const paginatedNotifications = computed<Notification[]>(() => {
      const start = (state.pager.currentPage - 1) * state.pager.itemsPerPage;
      const end = start + state.pager.itemsPerPage;
      return props.notifications.slice(start, end);
    });
    const shouldShowDeleteModal = computed<boolean>(() => {
      return state.showDeleteModal;
    });

    const doSearch = (): void => {
      emit('search-notification', state.search);
    };
    const openUpdateModal = (notification: Notification): void => {
      emit('update-notification', notification);
    };
    const openDeleteModal = (notification: Notification): void => {
      state.notificationToDelete = notification;
      state.showDeleteModal = true;
    };
    const confirmDelete = (): void => {
      if (!state.notificationToDelete) return;

      try {
        state.isLoading = true;
        emit('delete-notification', state.notificationToDelete.id);
      } finally {
        state.isLoading = false;
        state.notificationToDelete = null;
        state.showDeleteModal = false;
      }
    };
    const cancelDelete = (): void => {
      state.notificationToDelete = null;
      state.showDeleteModal = false;
    };
    const getNotificationStatusStyle = (notification: Notification): NotificationStatusStyle => {
      const now = new Date();
      const startAt = new Date(notification.publish_start_at);
      const endAt = notification.publish_end_at ? new Date(notification.publish_end_at) : null;
      if (!startAt || isNaN(startAt.getTime())) return { 'background-color': NOTIFICATION_COLORS.DEFAULT };
      if (startAt > now) return { 'background-color': NOTIFICATION_COLORS.UPCOMING };
      if (endAt && !isNaN(endAt.getTime()) && endAt < now) return { 'background-color': NOTIFICATION_COLORS.EXPIRED };

      return { 'background-color': NOTIFICATION_COLORS.DEFAULT };
    };

    return {
      state,
      // computed
      shouldShowDeleteModal,
      paginatedNotifications,
      // methods
      doSearch,
      openUpdateModal,
      openDeleteModal,
      confirmDelete,
      cancelDelete,
      getNotificationStatusStyle,
    };
  },
});
